import { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import 'bootstrap/dist/css/bootstrap.min.css';
import AccessCodeAuth from './AccessCodeAuth';

export default function RBMEvaluator() {
  const [input, setInput] = useState('');
  const [result, setResult] = useState(null);
  const [rewriteInput, setRewriteInput] = useState('');
  const [rewriteResult, setRewriteResult] = useState(null);
  const [currentTab, setCurrentTab] = useState('impact');
  const [sector, setSector] = useState('general');
  const [logframeText, setLogframeText] = useState('');
  const [logframeFeedback, setLogframeFeedback] = useState('');
  const [inputError, setInputError] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const gbvfPillars = {
    Accountability: ['justice', 'oversight', 'enforcement', 'leadership'],
    Prevention: ['norms', 'education', 'media', 'early intervention'],
    'Protection & Safety': ['shelters', 'health', 'policing', 'protocols'],
    'Justice & Law Reform': ['prosecution', 'saps', 'case flow', 'sentencing'],
    'Support & Healing': ['trauma', 'psychosocial', 'medical', 'tccs'],
    'Economic Power': ['livelihood', 'employment', 'empowerment', 'income']
  };

  // Check if user is already authenticated on component mount
  useEffect(() => {
    const storedAccessCode = sessionStorage.getItem('rbmAccessCode');
    if (storedAccessCode) {
      setIsAuthenticated(true);
    }
  }, []);

  const handleAuthSuccess = () => {
    setIsAuthenticated(true);
  };

  const callGrokAPI = async (type, input, sector) => {
    const endpoint = 'https://schoolofservice.co.za/backend/api.php';
    // Clean input: Remove unwanted prefixes and quotes
    const cleanInput = input.trim().replace(/^evaluate this one\s*[\'"]?|[\'"]?$/i, '');
    const payload = { type, input: cleanInput, sector };
    if (!type || !cleanInput) {
      throw new Error('Type or input is missing in payload.');
    }
    console.log('Sending payload:', JSON.stringify(payload));
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`PHP API request failed: ${response.status} - ${response.statusText} - ${errorText}`);
      }
      const data = await response.json();
      if (data.error) {
        throw new Error(data.error);
      }
      return data;
    } catch (error) {
      console.error('Fetch error:', error);
      throw error;
    }
  };

  const handleEvaluate = async (type) => {
    if (!input.trim()) {
      setInputError('Please enter a valid statement to evaluate.');
      setResult(null);
      setRewriteInput('');
      setRewriteResult(null);
      return;
    }

    setInputError('');
    setResult({ loading: true });
    try {
      const data = await callGrokAPI(type, input, sector);
      console.log('API response:', data); // Debug response
      setResult(data);
      setRewriteInput(data.revised);
      setRewriteResult(null);
    } catch (error) {
      setResult({ error: error.message });
      setRewriteInput('');
      setRewriteResult(null);
    }
  };

  const handleRewriteEvaluate = async (type) => {
    if (!rewriteInput.trim()) {
      setRewriteResult({ error: 'Please enter a suggested rewrite to evaluate.' });
      return;
    }

    setRewriteResult({ loading: true });
    try {
      const data = await callGrokAPI(type, rewriteInput, sector);
      console.log('Rewrite API response:', data); // Debug response
      setRewriteResult(data);
    } catch (error) {
      setRewriteResult({ error: error.message });
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      const preview = jsonData.map(row => row.join(' | ')).join('\n');
      setLogframeText(preview);
    };
    reader.readAsArrayBuffer(file);
  };

  const renderCritique = (critique) => {
    const lines = critique.split('\n').filter(line => line.trim());
    const summary = lines[0] || '';
    const bullets = lines.slice(1).filter(line => line.startsWith('-'));
    return (
      <>
        <p>{summary}</p>
        {bullets.length > 0 && (
          <ul>
            {bullets.map((bullet, index) => (
              <li key={index}>{bullet.substring(1).trim()}</li>
            ))}
          </ul>
        )}
      </>
    );
  };

  const renderEvaluation = (data, isRewrite = false) => {
    if (!data) return null;
    if (data.error) {
      return (
        <div className="alert alert-danger" style={{ whiteSpace: 'pre-line' }}>
          <strong>Error:</strong> {data.error}
        </div>
      );
    }
    if (data.loading) {
      return <div className="alert alert-info">Evaluating...</div>;
    }
    return (
      <div className={`alert ${isRewrite ? 'alert-warning' : 'alert-info'}`} style={{ whiteSpace: 'pre-line' }}>
        <h5>{isRewrite ? 'Rewrite Evaluation' : 'Original Evaluation'}</h5>
        <p><strong>Statement:</strong> "{data.original}"</p>
        <h6>🔍 AI Evaluation (RBM/M&E-Aligned):</h6>
        {renderCritique(data.critique)}
        <h6>✅ Suggested Rewrite:</h6>
        <p>{data.revised}</p>
        {data.analysis && data.analysis.length > 0 && (
          <>
            <h6>🧠 Why This Is Stronger:</h6>
            <table className="table table-bordered">
              <thead>
                <tr>
                  <th>Component</th>
                  <th>Example</th>
                  <th>Why it’s better</th>
                </tr>
              </thead>
              <tbody>
                {data.analysis.map((row, index) => (
                  <tr key={index}>
                    <td>{row.component}</td>
                    <td>{row.example || '-'}</td>
                    <td>{row.why}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    );
  };

  // If not authenticated, show the access code form
  if (!isAuthenticated) {
    return <AccessCodeAuth onAuthSuccess={handleAuthSuccess} />;
  }

  return (
    <div className="container py-5 d-flex justify-content-center align-items-center min-vh-100" style={{ background: '#f8fafc' }}>
      <div className="card shadow-lg p-5 w-100" style={{ maxWidth: 1100, background: '#fff', borderRadius: '2rem' }}>
        <div className="d-flex align-items-center mb-4">
          <span className="me-3" style={{ fontSize: '2.7rem', color: '#0d6efd' }}>
            <i className="bi bi-bar-chart-line-fill"></i>
          </span>
          <h1 className="mb-0 flex-grow-1 text-center" style={{ fontWeight: 800, letterSpacing: 1, fontSize: '2.5rem' }}>RBM & M&E Tool for CBOs</h1>
          <button
            className="btn btn-outline-secondary"
            onClick={() => {
              sessionStorage.removeItem('rbmAccessCode');
              setIsAuthenticated(false);
            }}
            title="Clear access and return to login"
          >
            <i className="bi bi-box-arrow-right"></i> Clear Access
          </button>
        </div>
        <div className="alert alert-info mb-5" style={{ fontSize: '1.25rem' }}>
          <strong>Welcome!</strong> This tool helps you craft and evaluate Results-Based Management (RBM) statements for Impact, Outcome, Output, and Indicators. Select a tab, enter your statement, and get instant AI-powered feedback and suggestions.
        </div>
        <div className="row mb-5">
          <div className="col-md-6 mx-auto">
            <label htmlFor="sectorSelect" className="form-label fw-bold" style={{ fontSize: '1.2rem' }}>Select Sector</label>
            <select
              id="sectorSelect"
              className="form-select mb-2 py-3 fs-5"
              value={sector}
              onChange={(e) => setSector(e.target.value)}
              style={{ fontSize: '1.1rem' }}
            >
              <option value="general">General</option>
              <option value="education">Education</option>
              <option value="gbvf">GBVF</option>
              <option value="democracy">Democracy</option>
              <option value="rights">Constitutional Rights</option>
              <option value="poverty">Poverty</option>
              <option value="movement">Movement Building</option>
            </select>
          </div>
        </div>
        <ul className="nav nav-tabs mb-5 justify-content-center" style={{ fontSize: '1.35rem' }}>
          <li className="nav-item">
            <button
              className={`nav-link ${currentTab === 'impact' ? 'active' : ''}`}
              onClick={() => { setCurrentTab('impact'); setInput(''); setResult(null); setRewriteInput(''); setRewriteResult(null); setInputError(''); }}
              style={{ minWidth: 160, padding: '1rem 0' }}
            >
              Impact
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${currentTab === 'outcome' ? 'active' : ''}`}
              onClick={() => { setCurrentTab('outcome'); setInput(''); setResult(null); setRewriteInput(''); setRewriteResult(null); setInputError(''); }}
              style={{ minWidth: 160, padding: '1rem 0' }}
            >
              Outcome
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${currentTab === 'output' ? 'active' : ''}`}
              onClick={() => { setCurrentTab('output'); setInput(''); setResult(null); setRewriteInput(''); setRewriteResult(null); setInputError(''); }}
              style={{ minWidth: 160, padding: '1rem 0' }}
            >
              Output
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${currentTab === 'indicator' ? 'active' : ''}`}
              onClick={() => { setCurrentTab('indicator'); setInput(''); setResult(null); setRewriteInput(''); setRewriteResult(null); setInputError(''); }}
              style={{ minWidth: 160, padding: '1rem 0' }}
            >
              Indicator
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${currentTab === 'logframe' ? 'active' : ''}`}
              onClick={() => { setCurrentTab('logframe'); setInput(''); setResult(null); setRewriteInput(''); setRewriteResult(null); setInputError(''); }}
              style={{ minWidth: 200, padding: '1rem 0' }}
            >
              Logframe Assessment
            </button>
          </li>
        </ul>
        <div className="tab-content">
          {['impact', 'outcome', 'output', 'indicator'].includes(currentTab) && (
            <div className="tab-pane fade show active">
              <div className="p-5 mb-5" style={{ background: '#f6fafd', borderRadius: '1.5rem', border: '2px solid #e3e8ee' }}>
                <div className="mb-4">
                  <label htmlFor="inputText" className="form-label fw-bold" style={{ fontSize: '1.25rem' }}>
                    {currentTab.charAt(0).toUpperCase() + currentTab.slice(1)} Statement
                  </label>
                  <textarea
                    id="inputText"
                    className="form-control mb-3 fs-5"
                    placeholder={`Enter your ${currentTab} (e.g., ${
                      currentTab === 'impact' ? 'Reduced GBVF through systemic change' :
                      currentTab === 'outcome' ? 'Increased access to shelters by 2025' :
                      currentTab === 'output' ? 'Number of awareness workshops conducted' :
                      'Number of survivors accessing psychosocial support'
                    }). Avoid prefixes like "evaluate this one".`}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    rows={5}
                    style={{ fontSize: '1.15rem', minHeight: 120 }}
                  />
                  {inputError && <div className="text-danger mt-1" style={{ fontSize: '1.1rem' }}>{inputError}</div>}
                  <small className="text-muted" style={{ fontSize: '1.05rem' }}>Note: Prefixes like "evaluate this one" will be automatically removed.</small>
                </div>
                <button
                  className="btn btn-primary btn-lg mb-4 w-100 fw-bold fs-4 py-3"
                  onClick={() => handleEvaluate(currentTab)}
                  disabled={result && result.loading}
                  style={{ letterSpacing: 1 }}
                >
                  {result && result.loading ? 'Evaluating...' : `Evaluate ${currentTab.charAt(0).toUpperCase() + currentTab.slice(1)}`}
                </button>
                {result && renderEvaluation(result, false)}
                {result && !result.error && !result.loading && (
                  <>
                    <hr className="my-5" />
                    <div className="mt-4">
                      <label htmlFor="rewriteText" className="form-label fw-bold" style={{ fontSize: '1.2rem' }}>
                        Your Suggested Rewrite
                      </label>
                      <textarea
                        id="rewriteText"
                        className="form-control mb-3 fs-5"
                        placeholder="Enter your revised statement..."
                        value={rewriteInput}
                        onChange={(e) => setRewriteInput(e.target.value)}
                        rows={3}
                        style={{ fontSize: '1.1rem', minHeight: 80 }}
                      />
                      <button
                        className="btn btn-outline-secondary btn-lg w-100 fw-bold fs-4 py-3"
                        onClick={() => handleRewriteEvaluate(currentTab)}
                        disabled={rewriteResult && rewriteResult.loading}
                        style={{ letterSpacing: 1 }}
                      >
                        {rewriteResult && rewriteResult.loading ? 'Evaluating...' : 'Evaluate Rewrite'}
                      </button>
                      {rewriteResult && renderEvaluation(rewriteResult, true)}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
          {currentTab === 'logframe' && (
            <div className="tab-pane fade show active">
              <div className="p-5 mb-5" style={{ background: '#f6fafd', borderRadius: '1.5rem', border: '2px solid #e3e8ee' }}>
                <div className="mb-4">
                  <label htmlFor="logframeText" className="form-label fw-bold" style={{ fontSize: '1.2rem' }}>Logframe Text</label>
                  <textarea
                    id="logframeText"
                    className="form-control mb-3 fs-5"
                    placeholder="Paste your full logframe text here (e.g., Impact | Outcome | Indicator)"
                    value={logframeText}
                    onChange={(e) => setLogframeText(e.target.value)}
                    rows={10}
                    style={{ fontSize: '1.1rem', minHeight: 160 }}
                  />
                </div>
                <div className="mb-4">
                  <label htmlFor="fileUpload" className="form-label fw-bold" style={{ fontSize: '1.2rem' }}>Upload Excel File</label>
                  <input
                    id="fileUpload"
                    type="file"
                    accept=".xlsx,.xls"
                    className="form-control mb-3 fs-5"
                    onChange={handleFileUpload}
                  />
                </div>
                <button
                  className="btn btn-primary btn-lg w-100 fw-bold fs-4 py-3"
                  onClick={() => alert('Logframe evaluation not implemented. Extend callGrokAPI and api.php to handle type=\"logframe\".')}
                  style={{ letterSpacing: 1 }}
                >
                  Assess Logframe
                </button>
                {logframeFeedback && (
                  <div className="alert alert-warning mt-4" style={{ whiteSpace: 'pre-line', fontSize: '1.15rem' }}>
                    {logframeFeedback}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}